import type { CategoryContent } from '../../row.js';

/** Wiskunde, technologie en sport, tweede reeks. Juiste antwoord staat eerst; wordt bij het bouwen gerandomiseerd. */

export const MORE_MATHS: CategoryContent = {
  msc: [
    [
      'Wat vereist de tussenwaardestelling van de functie?',
      [
        'Continuïteit op het gesloten interval',
        'Differentieerbaarheid op het open interval',
        'Monotoniciteit op het gesloten interval',
        'Begrensdheid van de afgeleide',
      ],
      0,
      'Continuïteit alleen. Differentieerbaarheid is wat de middelwaardestelling nodig heeft, en het is de gebruikelijke verwarring tussen de twee.',
    ],
    [
      'Een groep van priemorde p is noodzakelijkerwijs wat?',
      [
        'Cyclisch, en dus isomorf met de gehele getallen modulo p',
        'Abels maar niet noodzakelijk cyclisch',
        'Enkelvoudig maar niet noodzakelijk abels',
        'Isomorf met een deelgroep van de symmetrische groep op p letters, en niets anders',
      ],
      0,
      'Lagrange dwingt elk niet-identiek element om orde p te hebben, dus elk van hen genereert de hele groep.',
    ],
    [
      'Wat meet de determinant van een lineaire afbeelding meetkundig?',
      [
        'De ondertekende factor waarmee het volume wordt geschaald',
        'De lengte van de langste vector in het beeld van de eenheidsbol',
        'De som van de gekwadrateerde elementen van de bijbehorende matrix',
        'De hoek tussen het beeld en het origineel van een basisvector',
      ],
      0,
      'Daarom betekent een determinant van nul dat de afbeelding het volume laat instorten, en daarom komt tekenwisseling overeen met omkering van oriëntatie.',
    ],
    [
      'Wat betekent volledigheid in een metrische ruimte?',
      [
        'Elke Cauchy-rij convergeert naar een limiet binnen de ruimte',
        'Elke begrensde rij heeft een convergente deelrij',
        'Elke open overdekking heeft een eindige deeloverdekking',
        'De ruimte bevat geen geïsoleerde punten',
      ],
      0,
      'De rationale getallen zijn het standaardvoorbeeld van onvolledigheid, en de reële getallen zijn wat je krijgt door ze te completeren.',
    ],
    [
      'Wat is de convergentiestraal van de machtreeks voor de exponentiële functie?',
      ['Oneindig', 'Eén', 'Nul', 'Gelijk aan het grondtal van de exponent'],
      0,
      'De faculteit in de noemer overheerst elk vast argument, en daarom is exp overal analytisch.',
    ],
    [
      'Welk verband drukt de stelling van Bayes uit?',
      [
        'De posterior in termen van de aannemelijkheid, de prior en de marginale kans van de gegevens',
        'De gezamenlijke verdeling als product van onafhankelijke marginalen',
        'De verwachting van een som als de som van verwachtingen',
        'De variantie van een som in termen van covarianties, zonder enige aanname van onafhankelijkheid',
      ],
      0,
      'De inhoud is triviaal en het gebruik niet: alles wat moeilijk is, zit in het vastleggen van de prior en het berekenen van de marginaal.',
    ],
    [
      'Wat betekent het dat een matrix orthogonaal is?',
      [
        'Haar getransponeerde is haar inverse',
        'Haar kolommen zijn lineair onafhankelijk',
        'Haar eigenwaarden zijn allemaal reëel en niet-negatief',
        'Zij is diagonaliseerbaar over de reële getallen',
      ],
      0,
      'Determinant plus of min één volgt daaruit, en de twee tekens zijn respectievelijk rotaties en spiegelingen.',
    ],
    [
      'Wat is het verschil tussen een noodzakelijke en een voldoende voorwaarde?',
      [
        'Een noodzakelijke voorwaarde moet gelden voor de conclusie; een voldoende voorwaarde garandeert die',
        'Een noodzakelijke voorwaarde garandeert de conclusie; een voldoende voorwaarde staat die slechts toe',
        'Ze zijn logisch equivalente formuleringen',
        'Een noodzakelijke voorwaarde geldt voor universalia en een voldoende voor bijzonderheden',
      ],
      0,
      'Elementair en eindeloos verkeerd gebruikt. Beide samen vormen een dan-en-slechts-dan, wat de meeste interessante stellingen daadwerkelijk vaststellen.',
    ],
    [
      'Wat stelt het ladenprincipe?',
      [
        'Als n voorwerpen minder dan n vakjes bezetten, bevat een vakje er meer dan één',
        'Elke eindige verzameling kan worden verdeeld in deelverzamelingen van gelijke omvang',
        'Er bestaat een bijectie tussen elke twee aftelbaar oneindige verzamelingen',
        'Elke rij in een begrensde verzameling heeft een convergente deelrij',
      ],
      0,
      'Triviaal om te stellen en verantwoordelijk voor enkele werkelijk verrassende resultaten, waaronder de Ramsey-theorie.',
    ],
    [
      'Wat is de kenmerkende eigenschap van een Markovketen?',
      [
        'De volgende toestand hangt alleen af van de huidige toestand',
        'Alle toestanden worden op de lange termijn even vaak bezocht',
        'Overgangskansen veranderen deterministisch in de tijd',
        'Het proces keert met kans één terug naar zijn begintoestand',
      ],
      0,
      'Geheugenloosheid. Recurrentie en stationariteit zijn extra eigenschappen die een keten wel of niet kan hebben.',
    ],
  ],
  phd: [
    [
      'Wat levert de spectraalstelling voor compacte zelfgeadjungeerde operatoren op Hilbertruimte?',
      [
        'Een aftelbare orthonormale basis van eigenvectoren waarvan de eigenwaarden alleen bij nul accumuleren',
        'Een continu spectrum zonder eigenwaarden',
        'Een eindigdimensionale invariante deelruimte voor elke zo\'n operator',
        'Een unitaire equivalentie met een vermenigvuldigingsoperator op een eindige maatruimte',
      ],
      0,
      'Compactheid is wat het discrete spectrum afdwingt. Laat je die vallen, dan blijft het beeld van de vermenigvuldigingsoperator over.',
    ],
    [
      'Wat levert de stelling van Radon-Nikodym?',
      [
        'Een dichtheidsfunctie van de ene maat ten opzichte van een andere, gegeven absolute continuïteit',
        'Het bestaan van een productmaat op een productruimte',
        'Een ontbinding van elke maat in een discreet en een continu deel',
        'De uniciteit van de Lebesguemaat op schaling na',
      ],
      0,
      'Ze maakt "kansdichtheid" tot een stelling in plaats van een afspraak, en ze onderbouwt voorwaardelijke verwachting.',
    ],
    [
      'Wat is een martingaal, informeel gesteld?',
      [
        'Een proces waarvan de voorwaardelijke verwachting van de volgende waarde, gegeven het verleden, gelijk is aan de huidige waarde',
        'Een proces met onafhankelijke, identiek verdeelde toenames',
        'Een proces waarvan de variantie lineair groeit in de tijd, zoals een brownse beweging met constante diffusie',
        'Een proces dat oneindig vaak terugkeert naar zijn startwaarde',
      ],
      0,
      'De optionele-stoptijdstelling is de reden dat ze ertoe doen: ze maakt van "eerlijk spel" een rekenkundig hulpmiddel.',
    ],
    [
      'Wat stelt de stelling van Sard?',
      [
        'De verzameling kritieke waarden van een voldoend gladde afbeelding heeft maat nul',
        'Elke gladde afbeelding tussen variëteiten van gelijke dimensie is een lokaal diffeomorfisme',
        'Een gladde variëteit heeft een Morse-functie met onderscheiden kritieke punten',
        'Elke continue afbeelding tussen compacte variëteiten is homotoop met een gladde afbeelding',
      ],
      0,
      'Ze maakt "generieke" argumenten in de differentiaaltopologie legitiem - bijna elke waarde is een reguliere waarde.',
    ],
    [
      'Wat berekent de Mayer-Vietoris-rij in de algebraïsche topologie?',
      [
        'De homologie van een ruimte uit de homologieën van twee open verzamelingen die haar overdekken en hun doorsnede',
        'De homotopiegroepen van een fibratie uit die van vezel en basis',
        'De cohomologie van een productruimte uit haar factoren, via het tensorproduct van hun cohomologieën',
        'De Euler-karakteristiek uit een celontbinding',
      ],
      0,
      'Het fibratiegeval is de lange exacte rij van een fibratie, en het productgeval is Künneth - drie hulpmiddelen die vaak worden verward.',
    ],
    [
      'Wat is de betekenis van de stelling van Hahn-Banach?',
      [
        'Begrensde lineaire functionalen kunnen worden uitgebreid van een deelruimte naar de hele ruimte zonder toename van norm',
        'Elke begrensde operator op een Hilbertruimte bereikt zijn norm op de gesloten eenheidsbol van die ruimte',
        'Elke genormeerde ruimte is isometrisch inbedbaar in een Hilbertruimte',
        'Elke gesloten begrensde convexe verzameling in een Banachruimte is compact',
      ],
      0,
      'Zij is de reden dat dualiteitstheorie niet triviaal is, en het bewijs steunt in het algemene geval op het keuzeaxioma.',
    ],
    [
      'Wat vereist de centrale limietstelling naast onafhankelijkheid en identieke verdeling?',
      [
        'Eindige variantie',
        'Een eindig derde moment',
        'Een symmetrische verdeling',
        'Een continue verdelingsfunctie',
      ],
      0,
      'Zonder dat kom je in het regime van stabiele verdelingen terecht, en daarom breekt zwaar-staartige data zoveel standaardpraktijk.',
    ],
    [
      'Wat is de Galoiscorrespondentie voor een eindige Galois-uitbreiding?',
      [
        'Een inclusie-omkerende bijectie tussen deelgroepen van de Galoisgroep en tussenliggende lichamen',
        'Een isomorfisme tussen de Galoisgroep en de multiplicatieve groep van het lichaam',
        'Een bijectie tussen onherleidbare veeltermen en conjugatieklassen',
        'Een orde-behoudende afbeelding van deellichamen naar quotiëntgroepen',
      ],
      0,
      'Inclusie-omkering is het deel dat oplosbaarheid door middel van wortels equivalent maakt met oplosbaarheid van de groep.',
    ],
    [
      'Wat zegt de representatiestelling van Riesz voor een Hilbertruimte?',
      [
        'Elke begrensde lineaire functionaal is een inproduct met een unieke vector',
        'Elke zelfgeadjungeerde operator heeft een spectrale ontbinding',
        'Elke gesloten deelruimte heeft een orthogonaal complement',
        'Elke orthonormale basis heeft dezelfde cardinaliteit',
      ],
      0,
      'Zij maakt dat een Hilbertruimte canoniek isomorf is met zijn eigen duale, iets wat geen algemene Banachruimte kent.',
    ],
    [
      'Wat is de P-versus-NP-vraag, precies gesteld?',
      [
        'Of elk probleem waarvan oplossingen in polynomiale tijd verifieerbaar zijn, ook in polynomiale tijd oplosbaar is',
        'Of elk NP-volledig probleem een polynomiale-tijdbenadering toestaat',
        'Of niet-deterministische machines in enige resource strikt krachtiger zijn dan deterministische',
        'Of polynomiale-tijdalgoritmen bestaan voor alle problemen in PSPACE',
      ],
      0,
      'Verifiëren versus oplossen. Niet-determinisme is krachtiger voor ruimte in een andere zin, wat de vraag over machinekracht een echte maar afzonderlijke vraag maakt.',
    ],
  ],
  professor: [
    [
      'Wat relateert de indexstelling van Atiyah-Singer?',
      [
        'De analytische index van een elliptische operator aan een topologische invariant van de variëteit',
        'De Euler-karakteristiek aan de integraal van de kromming',
        'De signatuur van een variëteit aan haar doorsnijdingsvorm, wat de eerdere stelling van Hirzebruch is',
        'De dimensie van een modulieruimte aan het geslacht van een kromme',
      ],
      0,
      'Gauss-Bonnet en Riemann-Roch vallen er beide als bijzonder geval uit, wat een goede maatstaf is voor haar reikwijdte.',
    ],
    [
      'Wat is het Langlandsprogramma, in grote lijnen?',
      [
        'Een web van vermoede overeenkomsten tussen automorfe representaties en Galoisrepresentaties',
        'Een programma om alle eindige enkelvoudige groepen te classificeren',
        'Een raamwerk om de Riemann-hypothese te bewijzen via symmetrieën van zetafuncties',
        'Een vermoede classificatie van algebraïsche oppervlakken op birationale equivalentie na',
      ],
      0,
      'Het bewijs van de laatste stelling van Fermat loopt via een geval hiervan, wat laat zien hoe een vermoed programma concrete gevolgen kreeg.',
    ],
    [
      'Wat vestigt het bewijs van de Weil-vermoedens door Deligne?',
      [
        'Eigenschappen van zetafuncties van variëteiten over eindige lichamen',
        'De Riemann-hypothese voor de klassieke zetafunctie',
        'Het vermoeden van Mordell over rationale punten op krommen',
        'De rationaliteit van het Hodge-vermoeden voor abelse variëteiten',
      ],
      0,
      'Grothendieck bouwde de étale cohomologie grotendeels hiervoor, en Deligne voltooide het in 1974.',
    ],
    [
      'Wat is de betekenis van de classificatie van eindige enkelvoudige groepen?',
      [
        'Een volledige lijst in achttien families plus zesentwintig sporadische groepen, samengesteld door vele auteurs over duizenden pagina\'s',
        'Een bewijs dat elke eindige enkelvoudige groep van priemorde of alternerend is',
        'Een enkel verenigd bewijs van ongeveer tweehonderd pagina\'s, geleverd nadat de sporadische groepen daar los van waren opgesomd',
        'Een classificatie die alleen geldt voor groepen van oneven orde',
      ],
      0,
      'Het geval van oneven orde is Feit-Thompson, een vroeg en enorm ingrediënt. Het tweede-generatieproject om het geheel te bekorten loopt nog steeds.',
    ],
    [
      'Wat beperkt de stelling van Poincaré-Bendixson?',
      [
        'Het mogelijke langetermijngedrag van begrensde vlakke stromingen',
        'Het aantal limietcycli dat een polynomiaal vectorveld kan hebben',
        'De stabiliteit van periodieke banen onder kleine verstoring',
        'Het bestaan van invariante tori in Hamiltoniaanse systemen',
      ],
      0,
      'Ze verklaart waarom chaos in continue tijd drie dimensies nodig heeft, en waarom het Lorenz-systeem zo\'n verrassing was.',
    ],
    [
      'Wat is de inhoud van de stelling van Ax-Grothendieck?',
      [
        'Een injectieve polynomiale afbeelding van complexe affiene ruimte naar zichzelf is surjectief',
        'Elke polynomiale afbeelding tussen affiene variëteiten is eigenlijk',
        'Een bijectief morfisme van variëteiten is een isomorfisme',
        'Elke injectieve continue zelfafbeelding van de euclidische ruimte is een homeomorfisme',
      ],
      0,
      'Het standaardbewijs loopt via eindige lichamen en modeltheorie, wat het tot een geliefd voorbeeld maakt van overdracht tussen ogenschijnlijk verre gebieden.',
    ],
    [
      'Wat bewijst de stelling van Green-Tao?',
      [
        'De priemgetallen bevatten willekeurig lange rekenkundige rijen',
        'Er zijn oneindig veel priemparen die twee verschillen',
        'Elk voldoend groot even getal is een som van twee priemgetallen',
        'De gaten tussen opeenvolgende priemgetallen zijn oneindig vaak begrensd',
      ],
      0,
      '2004, met de stelling van Szemerédi plus een overdrachtsprincipe. Begrensde gaten is het werk van Zhang en het polymath-project, en dat is een afzonderlijke prestatie.',
    ],
    [
      'Wat is een topos, en waarom is dat belangrijk?',
      [
        'Een categorie die zich gedraagt als een categorie van schoven, en die een setting biedt waarin logica en meetkunde samenvallen',
        'Een categorie met alle eindige limieten en colimieten maar zonder exponentialen',
        'Een generalisatie van een topologische ruimte zonder onderliggende puntenverzameling',
        'Een model van de verzamelingenleer zonder het keuzeaxioma, waarin elke verzameling alleen uit de ordinalen construeerbaar is',
      ],
      0,
      'Interne logica is de winst: een topos heeft er een, en die hoeft niet klassiek te zijn, wat hem tot een geschikte plek voor constructieve wiskunde maakt.',
    ],
    [
      'Wat stelt het Hodge-vermoeden?',
      [
        'Bepaalde rationale cohomologieklassen op een projectieve variëteit zijn combinaties van klassen van algebraïsche deelvariëteiten',
        'Elke compacte Kähler-variëteit is projectief, zodat de analytische en de algebraïsche categorie daar volledig samenvallen',
        'De Hodge-ontbinding bestaat voor elke compacte complexe variëteit',
        'Elke algebraïsche cyclus is homoloog met een gladde',
      ],
      0,
      'Een van de Millennium-problemen. De Hodge-ontbinding zelf is een stelling voor Kähler-variëteiten; het vermoeden gaat over welke klassen algebraïsch zijn.',
    ],
    [
      'Wat stelt de stelling van Banach-Alaoglu, en waarom is ze nuttig?',
      [
        'De gesloten eenheidsbol van een duale ruimte is weak-star-compact',
        'De eenheidsbol van elke Banachruimte is zwak compact',
        'Elke begrensde rij in een Banachruimte heeft een in norm convergente deelrij',
        'De duale van een separabele ruimte is separabel',
      ],
      0,
      'Weak-star- in plaats van normcompactheid is precies het punt: zo krijgen variatieproblemen oplossingen wanneer geen in norm convergente deelrij bestaat.',
    ],
  ],
};

export const MORE_TECHNOLOGY: CategoryContent = {
  msc: [
    [
      'Wat beïnvloedt de belastingsfactor van een hashtabel?',
      [
        'Het verwachte aantal botsingen',
        'Het totale geheugengebruik, onafhankelijk van botsingsgedrag',
        'De volgorde waarin sleutels worden geretourneerd bij iteratie',
        'De worst-casekosten van het berekenen van de hashfunctie',
      ],
      0,
      'Daarom herformatteren implementaties bij een drempelwaarde in plaats van wanneer de tabel vol is, en daarom telt de geamortiseerde analyse.',
    ],
    [
      'Welk probleem lost NAT-doorgang op?',
      [
        'Het tot stand brengen van een directe verbinding tussen hosts achter adresvertalers die ongevraagde binnenkomende pakketten blokkeren',
        'Het vertalen tussen IPv4- en IPv6-adresfamilies, zodat hosts op elk protocol elkaar rechtstreeks kunnen adresseren',
        'Het toewijzen van privéadressen aan hosts op een lokaal netwerk',
        'Het routeren tussen autonome systemen met overlappende adresruimte',
      ],
      0,
      'STUN ontdekt de mapping, TURN relayt wanneer ontdekking mislukt, ICE probeert de kandidaten in volgorde.',
    ],
    [
      'Welke garantie geeft de duurzaamheidseigenschap van een databasetransactie?',
      [
        'Een vastgelegde transactie overleeft latere crashes',
        'Gelijktijdige transacties kunnen elkaars tussentoestand niet waarnemen',
        'Een transactie wordt volledig toegepast of helemaal niet',
        'De database schendt nooit een gedeclareerde beperking',
      ],
      0,
      'De andere drie zijn isolatie, atomiciteit en consistentie - en duurzaamheid is degene die uitmondt in een discussie over fsync.',
    ],
    [
      'Wat voegt public-key-infrastructuur toe aan cryptografie met rauwe publieke sleutels?',
      [
        'Een vertrouwensketen die sleutels aan identiteiten koppelt via certificaatautoriteiten',
        'Forward secrecy voor individuele sessies',
        'Weerstand tegen kwantumaanvallen op het onderliggende primitief',
        'Een mechanisme voor symmetrische-sleutelafleiding',
      ],
      0,
      'Het koppelingsprobleem is het moeilijke deel: de wiskunde zegt nooit van wie een sleutel is, en certificate transparency bestaat omdat certificaatautoriteiten dat verkeerd doen.',
    ],
    [
      'Wat is het doel van een write-ahead log?',
      [
        'Voorgenomen wijzigingen duurzaam vastleggen voordat ze worden toegepast, zodat herstel ze kan herhalen of ongedaan maken',
        'Schrijfacties in het geheugen bufferen om de doorvoer te verbeteren',
        'Wijzigingen naar een secundair systeem repliceren voor leesschaling',
        'Oude data compacteren om ruimte terug te winnen, door overlappende segmenten op de achtergrond samen te voegen',
      ],
      0,
      'Het zet willekeurige in-place-updates om in sequentiële toevoegingen, wat net zo goed een prestatie-eigenschap is als een correctheidseigenschap.',
    ],
    [
      'Wat gebruikt content-addressable storage als sleutel voor een object?',
      [
        'Een cryptografische hash van de inhoud van het object',
        'Een monotoon toenemende identifier die bij het schrijven wordt toegekend',
        'Een pad binnen een hiërarchische naamruimte',
        'Een UUID gegenereerd door de schrijvende client',
      ],
      0,
      'Deduplicatie en integriteitscontrole komen gratis mee; de prijs is dat het bijwerken van een object zijn naam verandert, en daarom ziet Git eruit zoals het eruitziet.',
    ],
    [
      'Wat betekent de term "idempotent" voor een API-bewerking?',
      [
        'Ze meer dan eens toepassen heeft hetzelfde effect als haar één keer toepassen',
        'Ze wijzigt nooit de servertoestand',
        'Ze geeft bij elke aanroep dezelfde responsbody terug',
        'Ze kan veilig parallel met zichzelf worden uitgevoerd',
      ],
      0,
      'Dat maakt opnieuw proberen na een timeout veilig. Veilig-en-alleen-lezend is een andere eigenschap, en parallelle veiligheid is een derde.',
    ],
    [
      'Wat doet een reverse proxy dat een forward proxy niet doet?',
      [
        'Hij staat voor servers en verschijnt aan clients als de oorspronkelijke bron',
        'Hij cachet responses namens clients',
        'Hij beëindigt TLS-verbindingen',
        'Hij herschrijft requestheaders voordat hij ze doorstuurt',
      ],
      0,
      'Beide kunnen cachen, TLS beëindigen en headers herschrijven. De richting van de anonimiteit - wiens identiteit verborgen wordt - is het werkelijke onderscheid.',
    ],
    [
      'Wat is de praktische betekenis van de p99-latentie van een systeem?',
      [
        'Ze vangt staartgedrag dat het gemiddelde verbergt',
        'Het is de latentie die door negenennegentig procent van de verzoeken wordt overschreden',
        'Het is de maximale latentie die het systeem onder elke belasting kan produceren',
        'Ze is gelijk aan drie standaarddeviaties boven het gemiddelde bij normale belasting',
      ],
      0,
      'Een verzoek dat honderd diensten aanraakt, raakt ongeveer één keer per verzoek zijn p99, en daarom stapelt staartlatentie zich op in plaats van uit te middelen.',
    ],
    [
      'Waarvoor gedraagt een semafoor die op één is geïnitialiseerd zich?',
      [
        'Een lock voor onderlinge uitsluiting, al zonder eigenaarschapssemantiek',
        'Een conditievariabele met één wachtende',
        'Een read-write-lock die gelijktijdige lezers toestaat',
        'Een barrière die twee threads synchroniseert',
      ],
      0,
      'Het ontbrekende eigenaarschap is de reden dat een semafoor kan worden vrijgegeven door een thread die hem nooit heeft verkregen - handig, en een rijke bron van fouten.',
    ],
  ],
  phd: [
    [
      'Wat garandeert het Paxos-algoritme, en wat niet?',
      [
        'Veiligheid onder asynchronie en uitval',
        'Zowel veiligheid als levendigheid onder volledige asynchronie',
        'Altijd levendigheid, met veiligheid alleen onder een meerderheidsquorum',
        'Veiligheid alleen wanneer alle knooppunten correct zijn',
      ],
      0,
      'FLP verbiedt de sterkere claim. Het scenario van dubbele voorstellers is de concrete manier waarop levendigheid faalt.',
    ],
    [
      'Wat is het verschil tussen snapshot-isolatie en serialiseerbaarheid?',
      [
        'Snapshot-isolatie staat write skew toe',
        'Snapshot-isolatie staat vuile lezingen van niet-vastgelegde data toe',
        'Snapshot-isolatie verbiedt gelijktijdige lezingen van dezelfde rij',
        'Snapshot-isolatie kan langlopende alleen-lezen-query\'s niet ondersteunen',
      ],
      0,
      'De beperking die geen van beide transacties afzonderlijk overtreedt maar samen wel, is het klassieke voorbeeld van write skew.',
    ],
    [
      'Wat ruilt een log-structured merge tree in tegen een B-boom?',
      [
        'Hogere schrijfdoorvoer door sequentiële batching',
        'Hogere leesdoorvoer, tegen de prijs van schrijfversterking',
        'Sterkere consistentiegaranties, tegen de prijs van geheugengebruik',
        'Lager ruimtegebruik, tegen de prijs van hogere latentievariatie',
      ],
      0,
      'RocksDB en Cassandra staan aan de ene kant, traditionele relationele indexen aan de andere, en compactieplanning is waar LSM-tuning zich daadwerkelijk afspeelt.',
    ],
    [
      'Wat lost consistente hashing op?',
      [
        'Herverdeling van slechts een klein deel van de sleutels wanneer de knooppuntenverzameling verandert',
        'Uniforme verdeling van sleutels over een vaste verzameling knooppunten',
        'Botsingsbestendigheid van de hashfunctie onder vijandige invoer',
        'Ordening van sleutels om bereikquery\'s te ondersteunen',
      ],
      0,
      'Virtuele knooppunten verhelpen het probleem van scheve belasting dat de naïeve ringversie heeft.',
    ],
    [
      'Welk head-of-line-blockingprobleem loste HTTP/2 niet op?',
      [
        'Gemultiplexte streams delen nog steeds één TCP-verbinding',
        'Serverpush kon niet door clients worden uitgeschakeld',
        'Headercompressietoestand moest per stream worden bijgehouden',
        'Prioriteringshints waren adviserend en werden breed genegeerd',
      ],
      0,
      'En precies daarom stapte HTTP/3 over op QUIC over UDP: verliesherstel per stream vereist dat het transport weet van streams.',
    ],
    [
      'Wat betekent de term "exactly-once delivery" in de praktijk daadwerkelijk?',
      [
        'At-least-once-aflevering plus idempotente verwerking of deduplicatie - de netwerkgarantie zelf is onbereikbaar',
        'Een garantie op transportniveau geboden door TCP-retransmissie',
        'Een eigenschap van berichtenmakelaars met persistente opslag',
        'Een gevolg van totaal geordende broadcast, die elk bericht precies eenmaal aan elk correct proces in dezelfde volgorde aflevert',
      ],
      0,
      'De onmogelijkheid volgt uit het probleem van de twee generaals. Alles wat als exactly-once wordt verkocht, is effectively-once aan de applicatiegrens.',
    ],
    [
      'Waarvoor wordt een vectorklok gebruikt?',
      [
        'Het detecteren van causale relaties en gelijktijdigheid tussen gebeurtenissen over processen heen',
        'Het synchroniseren van fysieke klokken over een cluster',
        'Het volledig ordenen van gebeurtenissen, zonder resterende ambiguïteit tussen gelijktijdige gebeurtenissen',
        'Het meten van verstreken tijd tussen gedistribueerde bewerkingen',
      ],
      0,
      'Ze geeft een partiële ordening en kan gelijktijdigheid detecteren, wat een Lamportklok niet kan - tegen de prijs van een grootte lineair in het aantal processen.',
    ],
    [
      'Waarvan hangt de rol van speculatieve executie bij Spectre-achtige aanvallen af?',
      [
        'Microarchitecturale toestand die blijft bestaan na verkeerd voorspeld werk dat architecturaal wordt weggegooid',
        'Directe lezingen van beschermd geheugen door niet-geprivilegieerde instructies',
        'Raceconditions in de paginatabelafhandeling van het besturingssysteem',
        'Timingvariabiliteit in de instructiedecoder, die de operanden lekt die op dat moment worden gedecodeerd',
      ],
      0,
      'Cachetoestand is het klassieke kanaal. Meltdown is de andere, meer directe fout-gebaseerde variant, en de twee door elkaar halen komt vaak voor.',
    ],
    [
      'Welk probleem lost een op Merkle-boom gebaseerd anti-entropieprotocol efficiënt op?',
      [
        'Bepalen welke sleutelbereiken tussen twee replica\'s verschillen zonder alle sleutels te versturen',
        'Het ordenen van gelijktijdige schrijfacties naar dezelfde sleutel',
        'Het comprimeren van gerepliceerde data tijdens transport, zodat bandbreedte in plaats van vergelijkingskosten domineert',
        'Het detecteren van Byzantijns gedrag door een replica',
      ],
      0,
      'Dynamo maakte het populair. Je daalt alleen af in de deelbomen waarvan de hashes verschillen, zodat de kosten schalen met het verschil in plaats van met de dataset.',
    ],
    [
      'Wat identificeert "tail at scale" als de dominante bron van latentievariabiliteit?',
      [
        'Concurrentie om gedeelde resources, achtergrondonderhoud en wachtrijen, versterkt door fan-out',
        'Voortplantingsvertraging in het netwerk tussen datacenters',
        'Garbage-collectionpauzes in managed runtimes, en uitsluitend die',
        'Schijfzoeklatentie in de opslaglaag, die overheerst wanneer de werkset de cache mist',
      ],
      0,
      'Dean en Barroso, 2013. Hedged requests en micropartitionering zijn de tegenmaatregelen die het voorstelt, en ze zijn nu standaardpraktijk.',
    ],
  ],
  professor: [
    [
      'Wat vereist de "beschikbaarheid" van het CAP-theorema precies?',
      [
        'Elk verzoek aan een niet-falend knooppunt krijgt een non-error-respons, zonder enige latentiegrens',
        'Het systeem blijft altijd bereikbaar vanuit elke client',
        'Verzoeken worden voltooid binnen een vastgesteld latentiebudget',
        'Een meerderheid van de knooppunten kan schrijfacties blijven accepteren, zodat een minderheidspartitie legitiem dienst mag weigeren',
      ],
      0,
      'Het ontbreken van een latentiegrens is de reden dat PACELC bestaat: in de praktijk is de afruil tijdens normale werking latentie tegen consistentie, niet beschikbaarheid.',
    ],
    [
      'Wat is de betekenis van het feit dat lineariseerbare registers universeel zijn?',
      [
        'Dat zijn ze niet - consensus kan niet worden opgebouwd uit alleen lees-schrijfregisters',
        'Elk gelijktijdig object kan wait-free worden geïmplementeerd uit registers',
        'Registers en compare-and-swap hebben hetzelfde consensusgetal',
        'Consensus is oplosbaar met registers, alleen met randomisatie in synchrone systemen',
      ],
      0,
      'Herlihy\'s consensusgetal voor registers is één; compare-and-swap is oneindig, en daarom heeft elke echte lock-free structuur een sterk primitief nodig.',
    ],
    [
      'Wat laat het FLP-resultaat open, en hoe benutten echte systemen dat?',
      [
        'Het verbiedt alleen deterministische afronding onder volledige asynchronie',
        'Het verbiedt consensus volledig, zodat echte systemen het slechts benaderen',
        'Het geldt alleen voor Byzantijnse fouten, niet voor crashes',
        'Het geldt alleen bij onbegrensde aantallen deelnemers',
      ],
      0,
      'Het gerandomiseerde protocol van Ben-Or en de foutdetectoren van Chandra-Toueg zijn de twee klassieke ontsnappingsroutes, en Raft kiest de route van partiële synchronie.',
    ],
    [
      'Wat is de relatie tussen de calculus van communicerende systemen en pi-calculus?',
      [
        'Pi-calculus breidt procescalculus uit met mobiliteit',
        'Pi-calculus beperkt CCS tot uitsluitend synchrone communicatie',
        'CCS is een getypeerde verfijning van de ongetypeerde pi-calculus',
        'Ze zijn notationele varianten met identieke expressiviteit',
      ],
      0,
      'Naamdoorgifte maakt pi-calculus in staat om dynamisch herconfigurerende systemen te modelleren, iets wat CCS niet kan.',
    ],
    [
      'Wat maakt homomorfe encryptie mogelijk, en wat blijft het obstakel?',
      [
        'Berekeningen op cijfertekst zonder decryptie',
        'Encryptie waarvan de sleutel kan worden geroteerd zonder data opnieuw te versleutelen',
        'Verificatie van een berekening zonder die opnieuw uit te voeren',
        'Zoeken in versleutelde data zonder enige informatielekkage',
      ],
      0,
      'Gentry\'s bootstrapping maakte volledig homomorfe schema\'s mogelijk in 2009; de constante factoren zijn wat ze nichetoepassingen houdt. Verifieerbare berekening is een ander primitief.',
    ],
    [
      'Wat beveelt het "end-to-end-argument" in systeemontwerp aan?',
      [
        'Functies die applicatiekennis vereisen, horen bij de eindpunten en niet in het netwerk',
        'Elke laag moet betrouwbaarheid onafhankelijk implementeren',
        'Netwerken moeten in alle omstandigheden staatloos zijn, omdat elke toestand die in transit wordt gehouden verloren kan gaan',
        'Beveiliging moet op de laagst mogelijke laag worden afgedwongen',
      ],
      0,
      'Saltzer, Reed en Clark, 1984. Het is een argument over waar kennis zich bevindt, en middleboxes zijn de voortdurende tegendruk.',
    ],
    [
      'Wat is de praktische betekenis van de stelling van Rice voor statische analyse?',
      [
        'Alle niet-triviale semantische eigenschappen zijn onbeslisbaar',
        'Statische analyse is onmogelijk voor talen met dynamische dispatch',
        'Typeinferentie is onbeslisbaar voor elke Turing-complete taal',
        'Programma-equivalentie is beslisbaar voor alleen terminerende programma\'s',
      ],
      0,
      'Soundness versus completeness is de resulterende technische keuze, en elke echte analyser kiest er een en rapporteert de andere als ruis.',
    ],
    [
      'Wat stelt een zero-knowledge-bewijs vast?',
      [
        'Dat een uitspraak waar is, zonder iets te onthullen buiten die waarheid',
        'Dat de bewijzer een geheim bezit, waarbij een commitment daarop wordt onthuld',
        'Dat een berekening correct is uitgevoerd, gegeven de invoer',
        'Dat twee partijen een gezamenlijke functie berekenden zonder vertrouwde derde partij',
      ],
      0,
      'Gezamenlijke berekening zonder vertrouwde derde partij is secure multiparty computation. Beknopte verificatie is een afzonderlijke eigenschap die SNARKs toevallig combineren met zero knowledge.',
    ],
    [
      'Wat is de fundamentele beperking van differentiële privacy zoals ze wordt toegepast?',
      [
        'Het privacybudget stapelt zich op over query\'s',
        'Ze kan niet worden toegepast op geaggregeerde statistieken',
        'Ze vereist in alle formuleringen een vertrouwde curator',
        'Ze biedt geen bescherming tegen een tegenstander met aanvullende informatie',
      ],
      0,
      'Robuustheid tegen aanvullende informatie is haar belangrijkste sterke punt. Lokale modellen schaffen de vertrouwde curator af tegen een grote prijs in nauwkeurigheid, en epsilon blijft de onopgeloste bestuurskwestie.',
    ],
    [
      'Wat verwijdert de "attention is all you need"-architectuur, en tegen welke prijs?',
      [
        'Recurrentie en convolutie',
        'Positionele informatie, tegen de prijs van ordeloosheid',
        'Niet-lineariteit, tegen de prijs van expressiviteit',
        'Parameterdeling, tegen de prijs van geheugengebruik',
      ],
      0,
      'Positionele codering herstelt de volgorde expliciet. De kwadratische kosten zijn waar de lange reeks efficiënte-attentiewerk sindsdien tegen strijdt.',
    ],
  ],
};

export const MORE_SPORT: CategoryContent = {
  msc: [
    [
      'In de Formule 1 beperkt het parc-fermé-reglement van de FIA, zodra de kwalificatie begint, teams tot wat?',
      [
        'Een korte, vooraf vastgestelde lijst van wijzigingen zoals bandenspanning, hoek van de voorvleugel en versleten remonderdelen',
        'Beslissen welke twee coureurs voor het racedeelname worden ingeschreven',
        'De volgorde kiezen waarin hun auto\'s de pitstraat verlaten tijdens de sessie',
        'Of teamradiocommunicatie met coureurs tijdens de sessie is toegestaan, en welke beperkingen de FIA aan de inhoud van berichten stelt',
      ],
      0,
      'Parc fermé vergrendelt de mechanische afstelling van een auto vanaf het begin van de kwalificatie tot de race. Alleen een korte, FIA-goedgekeurde lijst van wijzigingen is toegestaan: bandenspanning, voorvleugel-hoek, en rem-onderdelen.',
    ],
    [
      'Wat betekent een wind-geassisteerde prestatie in de atletiek?',
      [
        'Een prestatie met rugwind boven de 2,0 meter per seconde, niet in aanmerking komend voor records',
        'Een prestatie geleverd op een hoogte boven 1000 meter',
        'Een prestatie waarbij tempolichten zijn gebruikt',
        'Een prestatie geregistreerd met handmatige in plaats van elektronische tijdwaarneming',
      ],
      0,
      'Het geldt voor sprints tot en met 200 meter, het verspringen en het hinkstapspringen - onderdelen waar de assistentie meetbaar is in de aanloop.',
    ],
    [
      'Waarop is de buitenspelregel in het voetbal gebaseerd?',
      [
        'Positie op het moment dat de bal wordt gespeeld, ten opzichte van de voorlaatste verdediger en de bal',
        'Positie op het moment dat de bal door de aanvaller wordt ontvangen',
        'Of de aanvaller zich met het spel bemoeit, ongeacht positie',
        'Positie alleen ten opzichte van de laatste verdediger, waarbij de positie van de bal door de assistent-scheidsrechter volledig buiten beschouwing wordt gelaten',
      ],
      0,
      'De timing is wat het moeilijk maakt om te scheidsrechteren, en daarom richten semi-geautomatiseerde systemen zich op het frame van de pass.',
    ],
    [
      'Waarnaar verwijst "rating" in de roeisport?',
      [
        'Slagen per minuut',
        'De handicaptoeslag van de ploeg bij een gefaseerde start',
        'De verhouding tussen haaltijd en herstel',
        'De invalshoek van het blad bij de instap',
      ],
      0,
      'De verhouding tussen haaltijd en herstel is de "ratio", een afzonderlijke en vaak verwarde term.',
    ],
    [
      'Wat probeert een golfhandicapsysteem te bereiken?',
      [
        'Wedstrijden gelijk maken door scores aan te passen aan aangetoond vermogen',
        'Spelers internationaal rangschikken op toernooiprestaties',
        'De afslagposities bepalen die passen bij de slaglengte van een speler',
        'Het aantal clubs beperken dat een speler mag meenemen',
      ],
      0,
      'Het World Handicap System van 2020 verenigde zes regionale systemen, en course- en slope-rating maken een handicap overdraagbaar.',
    ],
    [
      'Wat is een "no-ball"-straf in het beperkte-overs-cricket?',
      [
        'Een extra run plus een free hit',
        'Twee extra runs waarbij de bal wel meetelt in de over',
        'Alleen een extra run, waarbij de slagman niet uit kan worden gegeven',
        'Een straf van vijf runs toegekend aan het slagveldteam',
      ],
      0,
      'De free hit werd ingevoerd om het afschrikmiddel te laten bijten, en daarom wordt de voetbal-no-ball automatisch beoordeeld.',
    ],
    [
      'Wat meet VO2max?',
      [
        'De maximale snelheid van zuurstofopname tijdens oplopende inspanning tot uitputting',
        'Het maximale luchtvolume dat per ademteug wordt verplaatst bij piekinspanning',
        'De zuurstofschuld die tijdens anaerobe arbeid wordt opgebouwd',
        'De efficiëntie waarmee zuurstof wordt omgezet in mechanisch werk',
      ],
      0,
      'De efficiëntiemaat is loop- of fietseconomie, en die onderscheidt prestaties bij getrainde atleten beter dan VO2max doet.',
    ],
    [
      'Wat is er nodig om een tiebreak bij zes-zes in het tennis te winnen?',
      [
        'Zeven punten met een marge van minstens twee',
        'Zeven punten ongeacht de marge',
        'Zes punten met een marge van minstens twee',
        'Tien punten met een marge van minstens twee',
      ],
      0,
      'Een standaard tiebreak vereist zeven punten met een marge van twee. De versie tot tien punten is een afzonderlijke regel, de matchtiebreak, gebruikt in plaats van een volledige beslissende set in sommige formats zoals dubbelspel.',
    ],
    [
      'Waarvoor is de "Bosman-uitspraak" van belang?',
      [
        'Het vestigen van gratis transfer bij contractverloop binnen de EU, en het afschaffen van nationaliteitsquota',
        'Het vestigen van een salarisplafond in de Europese clubcompetitie, afgedwongen door de bond bij alle aangesloten competities',
        'Het toestaan van eigendom van speciregistraties door derden',
        'Het verplichten van clubs om spelers vrij te geven voor interlandwedstrijden',
      ],
      0,
      '1995, Europees Hof van Justitie. Het veranderde de economie van het Europese voetbal meer dan enige wijziging in het competitieformat.',
    ],
    [
      'Waarnaar verwijst een "delta-tijd" in de Formule 1?',
      [
        'Een doeltijd die een coureur niet mag onderschrijden',
        'Het verschil tussen de beste en de gemiddelde ronde van een coureur',
        'De tijd die door een pitstop wordt verloren ten opzichte van doorrijden',
        'Het gat tussen kwalificatie- en racesnelheid',
      ],
      0,
      'Ze bestaat zodat coureurs niet kunnen winnen door door een gevaarlijke zone te razen, en ze wordt sector voor sector afgedwongen.',
    ],
  ],
  phd: [
    [
      'Onder de "50:22"-regel van rugby union, voor het eerst getest door World Rugby in 2021, wanneer wint het schoppende team de inworp bij de resulterende lineout?',
      [
        'Een schop vanuit de eigen helft van het schoppende team die stuit in het speelveld voordat hij binnen de 22 van de tegenstander uit gaat',
        'Een schop van waar dan ook op het veld die zonder te stuiten binnen de 22 van de tegenstander schoon wordt gevangen',
        'Een schop vanuit de eigen helft van de tegenstander die stuit voordat hij binnen de 22 van de tegenstander uit gaat',
        'Een schop die eerst binnen de 22 van de tegenstander stuit nadat hij over de doellijn van de tegenstander is gegaan en terug in het speelveld is gespeeld',
      ],
      0,
      'De regel beloont een specifieke schop: vanuit de eigen helft, die in het speelveld stuit en daarna binnen de 22 van de tegenstander uit gaat. Het dwingt verdedigers om aanvallende breedte niet dicht te zetten.',
    ],
    [
      'Welke langlopende beslissingsregel voor knock-outduels over twee wedstrijden in zijn Europese clubcompetities schafte de UEFA in 2021 af?',
      [
        'De uitdoelpuntenregel, waarbij een doelpunt gescoord uit thuis sinds 1965 een gelijkstand over twee wedstrijden had beslecht',
        'De verplichting tot een herkansingswedstrijd na een halve finale die na verlenging gelijk stond over twee wedstrijden',
        'De golden-goalregel die de verlenging in de tweede wedstrijd besliste',
        'Het zaaivoordeel dat aan de hoger gerangschikte club werd toegekend bij een gelijke stand over twee wedstrijden',
      ],
      0,
      'Ingevoerd in 1965 om aanvallend voetbal te belonen, maar tegen 2010 werkte het tegengesteld: het stimuleerde bezoekers tot verdediging. De UEFA schrapte het in 2021.',
    ],
    [
      'Wat is het belangrijkste statistische bezwaar tegen het gebruik van het conversiepercentage van strafschoppen om nemers te rangschikken?',
      [
        'Steekproefgroottes zijn miniem, zodat waargenomen percentages worden gedomineerd door ruis en sterk terugregeren naar het gemiddelde',
        'Het conversiepercentage is niet meetbaar uit wedstrijddata, omdat pogingen inconsistent worden geregistreerd tussen competities',
        'De vaardigheden van doelverdedigers zijn niet onafhankelijk tussen pogingen',
        'Strafschoppen worden niet-willekeurig toegekend ten opzichte van teamsterkte',
      ],
      0,
      'Een carrière van veertig strafschoppen ondersteunt bijna geen onderscheid tussen plausibele werkelijke percentages - de klassieke steekproef-valkuil in sportanalyse.',
    ],
    [
      'Wat modelleert de Duckworth-Lewis-Stern-methode, in structuur?',
      [
        'Resterende scoringsmiddelen als functie van zowel overs als resterende wickets samen',
        'Runs als lineaire functie van uitsluitend resterende overs',
        'De winstkans vanuit de huidige wedstrijdstaat',
        'Verwachte verloren wickets per over op elk moment van een innings',
      ],
      0,
      'De tweedimensionale tabel van middelen is de reden dat het herziene doel zelden pro rata is, en de Stern-herziening pakte hoog scorende moderne totalen aan.',
    ],
    [
      'Wat beschrijft de rek-verkortingscyclus in de biomechanica?',
      [
        'Versterkte concentrische kracht na snelle excentrische belasting',
        'De reciproque remming van antagonistische spieren bij snelle bewegingen',
        'De lengte-spanningsrelatie van een spier bij vaste activatie',
        'De afruil tussen kracht en snelheid bij concentrische samentrekking',
      ],
      0,
      'Daarom presteert een countermovementsprong beter dan een squatsprong, en daarom wordt plyometrische training überhaupt voorgeschreven.',
    ],
    [
      'Wat is de belangrijkste methodologische zwakte van de "marginal gains"-methodologie?',
      [
        'Individueel onwaarneembare effecten worden samen opgeteld zonder onafhankelijk bewijs voor het merendeel ervan',
        'Ze vereist apparatuur die voor de meeste atleten onbeschikbaar is',
        'Ze veronderstelt dat alle interventies dezelfde effectgrootte hebben',
        'Ze kan niet worden toegepast op teamsporten, waar individuele bijdragen niet los te maken zijn van het collectieve resultaat',
      ],
      0,
      'Achteraf toeschrijven aan een bundel interventies na een succesvol programma is geen bewijs voor enig specifiek onderdeel van die bundel.',
    ],
    [
      'Waarnaar verwijst het "relatieve-leeftijdseffect" in talentidentificatie?',
      [
        'Oververtegenwoordiging van atleten die vroeg in een selectiejaar zijn geboren',
        'De tendens van oudere atleten om jongere te overtreffen op elite-niveau',
        'De leeftijd waarop piekprestatie plaatsvindt in een gegeven sport',
        'Het voordeel van vroege specialisatie boven late diversificatie',
      ],
      0,
      'Het is zelfversterkend: vroege selectie geeft betere coaching, wat de volgende selectie rechtvaardigt - de underdogliteratuur stelt dat de benadeelde groep het op lange termijn beter doet.',
    ],
    [
      'Wat is de fysiologische basis van het "live high, train low"-protocol bij hoogtetraining?',
      [
        'Verblijf in hypoxie stimuleert erytropoëse, terwijl training op lage hoogte de absolute trainingsintensiteit behoudt',
        'Training op grote hoogte verbetert de looeconomie door veranderde biomechanica',
        'Hypoxie verhoogt de capillairdichtheid in spieren meer dan training op zeeniveau',
        'Slaap op lage hoogte verbetert het herstel van werk op grote hoogte, zodat de aanpassing behouden blijft terwijl vermoeidheid wordt weggewerkt',
      ],
      0,
      'Levine en Stray-Gundersen. Het ontwerp bestaat omdat het naïeve protocol intensiteit opoffert om de aanpassing te krijgen.',
    ],
    [
      'Wat regelt de K-factor van het Elo-systeem?',
      [
        'Hoe responsief ratings zijn op elk resultaat, wat gevoeligheid voor ruis afruilt tegen snelheid van aanpassing',
        'De verwachte score bij een gegeven ratingverschil, via de logistische functie die punten naar kans afbeeldt',
        'Het minimumaantal partijen voordat een rating voorlopig is',
        'De ratingondergrens waaronder een speler niet kan vallen',
      ],
      0,
      'FIDE varieert die naar rating en ervaring, wat een directe erkenning is dat één getal niet elke populatie kan bedienen.',
    ],
    [
      'Wat voorspelt de "onzekerheid-van-uitkomst"-hypothese in de sporteconomie?',
      [
        'De vraag stijgt met competitief evenwicht',
        'De omzet stijgt met het aantal gespeelde wedstrijden per seizoen',
        'De opkomst hangt vooral af van de prijselasticiteit van tickets',
        'De uitzendwaarde wordt gemaximaliseerd door talent te concentreren bij een paar clubs',
      ],
      0,
      'De empirische onderbouwing is gemengd en sportspecifiek - superstereffecten kunnen evenwicht overtroeven, wat lastig is voor de beleidsconclusies die er meestal uit worden getrokken.',
    ],
  ],
  professor: [
    [
      'De squad cost ratio van de UEFA, gefaseerd ingevoerd als onderdeel van de Financial Sustainability Regulations die Financial Fair Play vervingen, beperkt de uitgaven van een club aan lonen, transferamortisatie en makelaarshonoraria tot welk aandeel van relevante omzet zodra die volledig is ingefaseerd voor het seizoen 2025/26?',
      [
        '70%',
        '90%',
        '80%',
        '60%',
      ],
      0,
      'De squad cost ratio plafonneert lonen, transferamortisatie en makelaarshonoraria op 70% van voetbalgerelateerde omzet, geleidelijk ingefaseerd van 90% in 2023/24. Dit pakt het werkelijke mechanisme van financiële doping aan in plaats van break-evencijfers.',
    ],
    [
      'Wat stelde het Europees Hof van Justitie vast in zijn uitspraak van 2006 in de zaak Meca-Medina en Majcen tegen de Commissie?',
      [
        'Dat sportregels, inclusief antidopingregels, niet automatisch vrijgesteld zijn van het EU-mededingingsrecht alleen omdat ze sportspecifiek zijn',
        'Dat nationale antidopingorganisaties in elke EU-lidstaat identieke schorsingsduren moeten opleggen voor hetzelfde vergrijp',
        'Dat dopingsancties opgelegd door internationale bonden niet voor nationale rechters kunnen worden aangevochten',
        'Dat de doctrine van de "specificiteit van de sport" tucht- en toelaatbaarheidsregels van bonden volledig vrijstelt van de verplichtingen van de interne markt en het mededingingsrecht van de EU, ongeacht hun economisch effect op atleten',
      ],
      0,
      'Het Hof oordeelde dat sportieve regels met economische gevolgen niet automatisch buiten het mededingingsrecht vallen. Dit beperkte eerdere "puur sportieve" uitzonderingen.',
    ],
    [
      'In 2014 wijzigde de UCI fundamenteel de technische regels voor het uurrecord in het wielrennen. Wat was de wijziging?',
      [
        'Moderne aerodynamische tijdritmaterialen werden weer toegestaan, waarmee een einde kwam aan de zeventienjarige beperking tot de opstelling van Eddy Merckx uit 1972',
        'Het record werd permanent gesplitst in afzonderlijke categorieën "standaardmaterieel" en "bovenmenselijk", beoordeeld alleen op vermogensafgifte',
        'Binnenbaanpogingen werden verboden ten gunste van uitsluitend buitenbaanpogingen op de weg',
        'Er werd een minimale poogduur van vijfenveertig minuten ingevoerd, samen met een vereiste dat pogingen door twee continentale bonden onafhankelijk worden bekrachtigd, om korte, hoog-intensieve pogingen te diskwalificeren',
      ],
      0,
      'In 1997 bevroor de UCI het record op 1972-materiaal, maar in 2014 stond het modern aerodynamisch materiaal weer toe.',
    ],
    [
      'Waarop berust de biologische-paspoort-benadering van antidoping?',
      [
        'Longitudinale monitoring binnen dezelfde atleet van biomarkers, waarbij afwijkingen van een individuele basislijn worden gesignaleerd',
        'Directe detectie van verboden stoffen bij lagere drempels',
        'Populatiereferentiewaarden toegepast op elk testresultaat, waarbij elke waarde buiten de verdeling voor dat geslacht en die sport wordt gesignaleerd',
        'Retrospectieve analyse van opgeslagen monsters met nieuwere testmethoden',
      ],
      0,
      'Het leidt doping af uit het fysiologische gevolg in plaats van uit de stof zelf - en de resulterende bewijsstandaard is hard bevochten in rechtszaken.',
    ],
    [
      'Wat is de centrale moeilijkheid bij het reguleren van deelname van transgender- en DSD-atleten aan de elitesport?',
      [
        'Het afwegen van inclusie tegen een omstreden en onderdeelspecifiek bewijsbestand over behouden prestatievoordeel',
        'Het ontbreken van enig meetbaar prestatieverschil tussen groepen, zodra onderdeel en trainingsgeschiedenis onder controle zijn',
        'De onbeschikbaarheid van testosteronmeting onder wedstrijdomstandigheden',
        'Een gebrek aan juridische bevoegdheid over toelaatbaarheid bij internationale bonden',
      ],
      0,
      'De rechtszaak rond Semenya is waar de bewijsrechtelijke en juridische vragen samenkwamen, en de wetenschap verschilt daadwerkelijk per onderdeel en per maat.',
    ],
    [
      'Wat beweert onderzoek naar "tactische periodisering", en wat is het bewijsprobleem?',
      [
        'Training moet tactische, fysieke en psychologische dimensies integreren',
        'Fysieke en tactische training moeten strikt gescheiden worden in de weekcyclus',
        'Belasting moet een vast sinusoïdaal patroon volgen over een seizoen',
        'Kleine spelvormen zijn voldoende voor alle conditiebehoeften',
      ],
      0,
      'De beoefenaars behandelen holisme als een uitgangspunt, wat gecomponentgewijze randomisatie bijna per definitie onmogelijk maakt.',
    ],
    [
      'Wat meet de literatuur over "competitief evenwicht", en waarom is vergelijking tussen competities moeilijk?',
      [
        'Spreiding van winstpercentages',
        'Alleen het aantal verschillende kampioenen over een vaste periode',
        'Omzetverschil tussen de grootste en de kleinste clubs',
        'De correlatie tussen loonlijst en uiteindelijke competitiepositie',
      ],
      0,
      'De geïdealiseerde standaarddeviatie normaliseert voor de seizoenslengte, en daarom kunnen een competitie van tachtig en een van achtendertig wedstrijden überhaupt worden vergeleken.',
    ],
    [
      'Wat identificeert de literatuur over "scheidsrechtersbias" het meest robuust?',
      [
        'Thuisvoordeel bij discretionaire beslissingen, afgevlakt wanneer er geen publiek is',
        'Systematische bevooroordeling ten gunste van hoger gerangschikte teams, onafhankelijk van publiek',
        'Bias die alleen toe te schrijven is aan de nationaliteit van de scheidsrechter bij interlandwedstrijden',
        'Geen meetbare bias zodra de moeilijkheidsgraad van de beslissing onder controle is',
      ],
      0,
      'De wedstrijden zonder publiek van 2020 waren een ongeplande natuurlijke proef, en het publiekseffect op discretionaire beslissingen bleef daar grotendeels bestaan.',
    ],
    [
      'Wat is het analytische probleem met "clutch performance" als construct?',
      [
        'Zodra selectie en steekproefgrootte zijn verwerkt, is aanhoudend individueel clutch-vermogen zeer moeilijk te onderscheiden van ruis',
        'Situaties met hoge druk kunnen niet operationeel worden gedefinieerd, omdat winstkans en stand allebei continu veranderen door een wedstrijd heen',
        'Prestatiedata zijn niet beschikbaar voor de relevante momenten',
        'Het is aangetoond volledig afwezig te zijn in elke bestudeerde sport',
      ],
      0,
      'Het eerlijke standpunt is dat het effect, indien aanwezig, klein is ten opzichte van de ruis in elke carrièrelange steekproef - wat niet hetzelfde is als het als afwezig aantonen.',
    ],
    [
      'Wat concludeert de economie van het organiseren van megaevenementen in het algemeen?',
      [
        'Vooraf gemaakte projecties overtreffen systematisch de achteraf gemeten baten',
        'Organiseren levert betrouwbaar langdurige toerisme- en infrastructuurbaten op',
        'Effecten zijn neutraal in ontwikkelde en positief in ontwikkelingseconomieën',
        'Baten komen vooral voort uit exportbevorderende effecten',
      ],
      0,
      'Het werk van Baade en Matheson is de standaardverwijzing. De meest verdedigbare resterende claim is een ontastbaar feelgoodeffect, dat reëel is en moeilijk te prijzen.',
    ],
  ],
};
